import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Popover } from 'antd';
import { DateTime } from 'luxon';

import Permissions from '../../auth/Permissions';
import { getIdMap } from '../../helpers/helpers';
import { getAllDayValues } from '../../helpers/time';
import { getBackgroundColor } from '../scheduleHelpers';

const compactTime = (millis) => {
  if (!millis) return '';
  return DateTime.fromMillis(millis).toLocaleString(DateTime.TIME_SIMPLE).replace(':00', '');
};

const shiftTimeLabel = (shift) => {
  const {
    startTime,
    endTime,
    timezone = DateTime.local().zoneName,
  } = shift;
  if (!startTime) return '';
  const start = DateTime.fromMillis(startTime, { zone: timezone });
  const end = DateTime.fromMillis(endTime, { zone: timezone });
  const { isAllDay } = getAllDayValues(start, end);
  if (isAllDay) return 'All day';
  return compactTime(startTime);
};

const MoreShift = ({ shift, userMap, onClick }) => {
  const {
    title,
    users = [],
    color,
    isEvent,
    isDraft,
  } = shift;
  const isMyShift = users?.includes(Permissions.id);
  const backgroundColor = getBackgroundColor(users, userMap, color, isMyShift, isEvent, isDraft);

  return (
    <div
      className="schedule-month-more-shift"
      onClick={(e) => {
        e.stopPropagation();
        onClick(shift);
      }}
      role="button"
      tabIndex={-1}
      onKeyDown={() => {}}
    >
      <span
        className="schedule-shift-hover-card-dot"
        style={{ backgroundColor: backgroundColor || '#A1A1AA' }}
      />
      <span className="schedule-month-more-shift-copy">
        <span className="schedule-month-more-shift-title">{title}</span>
        <span className="schedule-month-more-shift-time">{shiftTimeLabel(shift)}</span>
      </span>
    </div>
  );
};

export default ({
  day,
  moreCount,
  onShiftSelect,
  multiDayShifts = [],
  interDayShifts = [],
}) => {
  const viewingShift = useSelector((state) => state.schedule.viewingShift);
  const stateUsers = useSelector((state) => state.users.users);
  const userMap = useMemo(() => getIdMap(stateUsers), [stateUsers]);
  const [visible, setVisible] = useState(false);
  const handleShiftClick = useCallback((shift) => {
    setVisible(false);
    onShiftSelect(shift);
  }, [onShiftSelect]);

  const shifts = useMemo(() => (
    [...multiDayShifts, ...interDayShifts].sort((a, b) => a.startTime - b.startTime)
  ), [multiDayShifts, interDayShifts]);

  useEffect(() => {
    if (viewingShift) setVisible(false);
  }, [viewingShift]);

  if (moreCount <= 0) return null;

  const shiftCountLabel = `${shifts.length} shift${shifts.length === 1 ? '' : 's'}`;

  return (
    <Popover
      visible={visible}
      trigger="click"
      placement="bottomLeft"
      onVisibleChange={setVisible}
      overlayClassName="schedule-month-more-popover"
      content={(
        <div className="schedule-month-more-card">
          <div className="schedule-month-more-date">
            {day.toFormat('cccc, LLLL d')}
          </div>
          <div className="schedule-month-more-count">{shiftCountLabel}</div>
          <div className="schedule-month-more-list">
            {shifts.map((shift) => (
              <MoreShift
                key={shift.id}
                shift={shift}
                userMap={userMap}
                onClick={handleShiftClick}
              />
            ))}
          </div>
        </div>
      )}
    >
      <div className="schedule-month-more">
        {`+${moreCount} more`}
      </div>
    </Popover>
  );
}
